/* eslint-disable complexity */
import React, { useState } from "react";
import styled from "styled-components";
import Form from "@ui-kit/form";
import Color from "@radarly/components/panels/card-edition/sidebar/color";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Card } from "@radarly/models/cards";
import { editDatavizOptionsOnCard } from "@radarly/models/cards/editor";
import {
  changeTermSelectionMethod,
  hasAttributeField,
  hasDatavizButton,
  hasManyMetricFields,
  hasMetricField,
  hasTitleCard,
  hasTwoAttributeFields,
} from "@radarly/models/cards/form";
import {
  isEmbeddedCard,
  isHiddenTermsCard,
  isPinnedAuthorCard,
  isPinnedPostCard,
  isPostsCard,
  isTermCard,
  isTextCard,
  isUsersCard,
} from "@radarly/models/cards/is";
import {
  CardTerm,
  TermCardTerm,
  getTermFromCard,
  getTermsFromPivotCard,
} from "@radarly/models/cards/term";
import {
  DatavizEditableColors,
  updateDatavizOptions,
} from "@radarly/models/dataviz";
import OptionsSidebar from "./options";
import TextOptions from "./options/text";
import ColorPickerPanel from "./panels/colorpicker";
import DatavizSelectorPanel from "./panels/dataviz";
import PivotSelectorPanel from "./panels/pivot";
import TermSelectorPanel from "./panels/term";
import DatavizButton from "./shared/dataviz";
import SharedEmbed from "./shared/embed";
import HiddenTerms from "./shared/hidden-terms";
import SharedMetric from "./shared/metric";
import SharedMetrics from "./shared/metrics";
import SharedPaginationSort from "./shared/pagination-sort";
import SharedPinnedAuthor from "./shared/pinned-author";
import SharedPinnedPost from "./shared/pinned-post";
import TermButton from "./shared/term";
import SharedTermSelection from "./shared/term-selection";
import SharedTitle from "./shared/title";
import SharedUserSort from "./shared/user-sort";

interface SharedPanelsCardEditionSidebarProps {
  card: Card;
  setCard: (card: Card) => void;
  aiContext: string;
  setAIContext: (description: string) => void;
  pageColors?: string[];
}

export enum PanelType {
  Viz = "dataviz",
  Term = "first_term",
  SecondTerm = "second_term",
  Color = "color",
}

const SidebarForm = styled(Form)`
  padding: 36px;
`;

const RadarlyPanelsCardEditionSidebar = ({
  card,
  setCard,
  aiContext,
  setAIContext,
  pageColors,
}: SharedPanelsCardEditionSidebarProps) => {
  const [panel, setPanel] = useState<{ type: PanelType; index?: number }>({
    type: null,
    index: 0,
  });
  const term = getTermFromCard(card);
  const { against } = getTermsFromPivotCard(card);
  const locale = useCurrentUserLocale();
  const closePanel = () => setPanel((p) => ({ ...p, type: null }));
  const onSetCard = (card: Card) => {
    setCard(card);
    closePanel();
  };
  return (
    <>
      {!!panel.type ? (
        <>
          {panel.type === PanelType.Viz && (
            <DatavizSelectorPanel
              card={card}
              setCard={onSetCard}
              closePanel={closePanel}
            />
          )}
          {panel.type === PanelType.Term && (
            <TermSelectorPanel
              card={card}
              setCard={onSetCard}
              closePanel={closePanel}
            />
          )}
          {panel.type === PanelType.SecondTerm && (
            <PivotSelectorPanel
              card={card}
              setCard={onSetCard}
              closePanel={closePanel}
            />
          )}
          {panel.type === PanelType.Color && (
            <ColorPickerPanel
              dataviz={card.dataviz as DatavizEditableColors}
              setDataviz={(dataviz) =>
                setCard(editDatavizOptionsOnCard(card, dataviz))
              }
              closePanel={closePanel}
              pageColors={pageColors}
              index={panel?.index}
            />
          )}
        </>
      ) : (
        <SidebarForm>
          {hasTitleCard(card) && (
            <SharedTitle card={card} setCard={setCard} locale={locale} />
          )}
          {isTextCard(card) && (
            <TextOptions card={card} setCard={setCard} locale={locale} />
          )}
          {hasAttributeField(card) && (
            <TermButton
              card={card}
              title={
                hasTwoAttributeFields(card)
                  ? tr(locale, "CUSTOM_PAGES::EDITOR::FIRST_ANALYZED_ATTRIBUTE")
                  : tr(locale, "CUSTOM_PAGES::EDITOR::ANALYZED_ATTRIBUTE")
              }
              openPanel={() =>
                setPanel((p) => ({ ...p, type: PanelType.Term }))
              }
              term={changeTermSelectionMethod(term, "frequency") as CardTerm}
            />
          )}
          {hasTwoAttributeFields(card) && (
            <TermButton
              card={card}
              title={tr(
                locale,
                "CUSTOM_PAGES::EDITOR::SECOND_ANALYZED_ATTRIBUTE"
              )}
              openPanel={() =>
                setPanel((p) => ({ ...p, type: PanelType.SecondTerm }))
              }
              term={changeTermSelectionMethod(against, "frequency") as CardTerm}
            />
          )}
          {hasDatavizButton(card) && (
            <DatavizButton
              card={card}
              openPanel={() => setPanel((p) => ({ ...p, type: PanelType.Viz }))}
            />
          )}
          {hasMetricField(card) && (
            <SharedMetric card={card} setCard={setCard} />
          )}
          {hasManyMetricFields(card) && (
            <SharedMetrics card={card} setCard={setCard} />
          )}
          {isPostsCard(card) && (
            <SharedPaginationSort card={card} setCard={setCard} />
          )}
          {isUsersCard(card) && (
            <SharedUserSort card={card} setCard={setCard} />
          )}
          {isTermCard(card) && (
            <SharedTermSelection
              card={card}
              setCard={setCard}
              dataviz={card.dataviz}
              term={term as TermCardTerm}
            />
          )}
          {isEmbeddedCard(card) && (
            <SharedEmbed card={card} setCard={setCard} locale={locale} />
          )}
          {isPinnedPostCard(card) && (
            <SharedPinnedPost card={card} setCard={setCard} locale={locale} />
          )}
          {isPinnedAuthorCard(card) && (
            <SharedPinnedAuthor card={card} setCard={setCard} locale={locale} />
          )}
          {isHiddenTermsCard(card) && (
            <HiddenTerms
              card={card}
              locale={locale}
              onDeleteTerm={(id) =>
                setCard(
                  editDatavizOptionsOnCard(
                    card,
                    updateDatavizOptions(card.dataviz, "hiddenTerms", {
                      id,
                      status: "exclude",
                    })
                  )
                )
              }
            />
          )}
          <OptionsSidebar
            dataviz={card.dataviz}
            setDataviz={(dataviz) =>
              setCard(editDatavizOptionsOnCard(card, dataviz))
            }
            setCard={setCard}
            card={card}
            aiContext={aiContext}
            setAIContext={setAIContext}
            setPanel={setPanel}
            renderColor={(dataviz) => (
              <Color
                title={tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::COLOR")}
                //@ts-ignore
                color={dataviz.color}
                setColor={(color) =>
                  setCard(
                    editDatavizOptionsOnCard(
                      card,
                      //@ts-ignore
                      updateDatavizOptions(card.dataviz, "color", color)
                    )
                  )
                }
                setPanel={setPanel}
                closePanel={closePanel}
                locale={locale}
              />
            )}
          />
        </SidebarForm>
      )}
    </>
  );
};

export default RadarlyPanelsCardEditionSidebar;
